import { useEffect, useRef, useState } from "react";
import { getChatHistory, streamChat } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Send, Sparkles, Terminal } from "lucide-react";

const STARTERS = [
  "I have 4 hours today — reorganize my schedule.",
  "I forgot RAG. Give me a 60-second refresher.",
  "I want to learn HR automation. Build me a plan.",
  "I learned Zapier today — what 3 projects should I build?",
];

export default function Assistant() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    getChatHistory().then((d) => setMessages(d.messages || [])).catch(() => {});
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streaming]);

  const send = (textArg) => {
    const text = (textArg ?? input).trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);
    streamChat(
      text, sessionId,
      (delta) => setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { ...copy[copy.length - 1], content: (copy[copy.length - 1].content || "") + delta };
        return copy;
      }),
      (sid) => setSessionId(sid),
      () => setStreaming(false),
      () => setStreaming(false),
    );
  };

  return (
    <div className="space-y-6 h-[calc(100vh-8rem)] flex flex-col">
      <div>
        <p className="overline">AI Coach</p>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight mt-2">
          <span className="text-primary terminal-glow">$</span> Ask anything. Get a plan.
        </h1>
        <p className="text-muted-foreground mt-1 text-sm">Powered by Gemini · knows your streak, XP, and current mission.</p>
      </div>

      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto border border-border rounded-md bg-card p-4 space-y-4 scanlines">
        {messages.length === 0 && (
          <div className="h-full grid place-items-center">
            <div className="text-center max-w-md space-y-4">
              <Terminal className="h-8 w-8 text-primary mx-auto terminal-glow" />
              <p className="mono text-sm text-muted-foreground">Try one of these:</p>
              <div className="grid gap-2">
                {STARTERS.map((s, i) => (
                  <Button key={i} variant="outline" data-testid={`starter-${i}`} className="border-border justify-start text-left mono text-xs h-auto py-3 whitespace-normal" onClick={() => send(s)}>
                    <Sparkles className="h-3 w-3 mr-2 shrink-0 text-primary" /> {s}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.role}-${i}`}>
            <div className={`max-w-[85%] rounded-md px-4 py-3 ${
              m.role === "user"
                ? "bg-muted border border-border text-foreground"
                : "bg-background border border-primary/40 text-foreground"
            }`}>
              <div className="mono text-[10px] tracking-[0.2em] uppercase mb-1 opacity-60">
                {m.role === "user" ? "> you" : "$ coach"}
              </div>
              <div className="whitespace-pre-wrap text-sm leading-relaxed">
                {m.content || (streaming && i === messages.length - 1 ? "▍" : "")}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex gap-2">
        <Textarea
          data-testid="chat-input"
          rows={2}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder='e.g. "I have 3 hours today"'
          className="bg-muted border-border resize-none mono text-sm"
          disabled={streaming}
        />
        <Button data-testid="chat-send-btn" onClick={() => send()} disabled={streaming || !input.trim()} className="bg-primary text-primary-foreground hover:bg-secondary hover:text-white h-auto px-4">
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
